// Slide-over panel from the right edge — opens when a SKU name is clicked
// in the grid. Shows: full per-location balances, the action buttons for
// quick-log, recent activity, and a link to the full /inventory/products/:id
// detail page (for trend chart, thresholds, etc).

import { useEffect, useState } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import { navigate } from '../lib/routing.js';
import { LOCATIONS as LOCS, locationLabel } from '../lib/locations.ts';
import { isElevated } from '../lib/auth.ts';
import { ConfirmDialog } from './ConfirmDialog.jsx';

const KIND_LABEL = { produced: '生产', received: '收货', sold: '售出', wasted: '报废', transferred: '调拨', adjusted: '调整' };
const REASON_LABEL = {
  expired: '过期', damaged: '损坏', overproduction: '多产', display: '展示',
  donated: '捐献', staff_taken: '店员取走', other: '其他',
  correction: '更正', opening_balance: '期初', variance: '差异'
};

export const InventorySlideOver = ({
  product, user, selectedDate, isToday = true, onClose, onLogEvent, onTransfer, onReset, onResetStock
}) => {
  const [events, setEvents] = useState(null);
  const [resets, setResets] = useState([]);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmStock, setConfirmStock] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Pull recent events + reset markers for THIS product, merge into one feed.
  // Returns a cancel flag so the mount effect can ignore a stale resolve.
  function loadFeed(isCancelled = () => false) {
    return Promise.all([
      api.listInventoryEvents({ product_id: product.id, limit: 25 }).catch(() => []),
      api.listProductResets(product.id, 25).catch(() => [])
    ]).then(([evs, rs]) => {
      if (isCancelled()) return;
      setEvents(evs);
      setResets(rs);
    });
  }
  useEffect(() => {
    let cancelled = false;
    loadFeed(() => cancelled);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  // Esc closes (Modal pattern reused for muscle memory).
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const elevated = isElevated(user);
  const canReset = elevated && typeof onReset === 'function';
  // 重设库存 zeroes the LIVE balance with a now-stamped 调整, so it only makes
  // sense on today. Offering it while reviewing a past day let the −调整 land on
  // the current day unexpectedly (Regina 2026-06-06). Past-day fixes use the
  // day-scoped 重设该日 above instead.
  const canResetStock = elevated && isToday && typeof onResetStock === 'function';
  // Off-today resets a past day; relabel so it's clear which day is affected.
  const resetLabel = isToday ? '重设今日数据' : `重设 ${selectedDate} 数据`;
  const dayPhrase = isToday ? '今天' : selectedDate;

  async function doReset() {
    setResetting(true);
    try {
      await onReset(product.id);
      await loadFeed();        // refresh the feed in place so the marker shows
      setConfirmReset(false);
    } finally {
      setResetting(false);
    }
  }

  async function doResetStock() {
    setResetting(true);
    try {
      await onResetStock(product.id);
      await loadFeed();        // the compensating 调整 rows show in the feed
      setConfirmStock(false);
    } finally {
      setResetting(false);
    }
  }

  // Merge events + resets into one timeline, newest first.
  const feed = events === null ? null : [
    ...events.map(e => ({ type: 'event', at: e.createdAt, key: `e${e.id}`, e })),
    ...resets.map(r => ({ type: 'reset', at: r.createdAt, key: `r${r.id}`, r }))
  ].sort((a, b) => String(b.at).localeCompare(String(a.at)));

  return (
    <>
      <div className="inv-slideover-backdrop" onClick={onClose} />
      <aside className="inv-slideover" onClick={(e) => e.stopPropagation()}>
        <div className="inv-slideover-head">
          <div>
            <h2 title={product.name}>{displayName(product)}</h2>
            <div className="vava-hint">
              {product.category || '—'}
              {product.variant ? ` · ${product.variant}` : ''}
              {product.size ? ` · ${product.size}` : ''}
              {product.shift === 'day' ? ' · 白班' : product.shift === 'night' ? ' · 晚班' : ''}
            </div>
          </div>
          <button className="close" onClick={onClose} aria-label="关闭">×</button>
        </div>

        <div className="inv-slideover-body">
          <div className="inv-slideover-section">
            <h3>当前库存</h3>
            <div className="inv-balances">
              {LOCS.map(l => {
                const qty = product.balances[l] || 0;
                return (
                  <div key={l} className={`inv-balance-cell ${qty === 0 ? 'zero' : ''}`}>
                    <span className="loc">{locationLabel(l)}</span>
                    <span className="qty">{qty}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="inv-slideover-section">
            <h3>最近活动</h3>
            {feed === null ? (
              <div className="vava-hint">加载中…</div>
            ) : feed.length === 0 ? (
              <div className="vava-hint">暂无记录。从主表点任意 SKU 单元格录入第一条。</div>
            ) : (
              <ul className="inv-feed">
                {feed.map(item => item.type === 'reset'
                  ? <ResetItem key={item.key} r={item.r} />
                  : <ActivityItem key={item.key} e={item.e} />)}
              </ul>
            )}
          </div>

          <div className="inv-slideover-section">
            <button className="btn btn-secondary"
                    onClick={() => { onClose(); navigate(`/inventory/products/${product.id}`); }}>
              查看 14 天趋势 + 阈值设置 →
            </button>
            {canReset && (
              <button className="btn btn-danger inv-reset-btn"
                      onClick={() => setConfirmReset(true)}>
                {resetLabel}
              </button>
            )}
            {canResetStock && (
              <button className="btn btn-danger inv-reset-btn"
                      onClick={() => setConfirmStock(true)}>
                重设库存
              </button>
            )}
          </div>
        </div>
      </aside>

      {confirmReset && (
        <ConfirmDialog
          title={resetLabel}
          kind="danger"
          confirmLabel={resetting ? '重设中…' : '确认重设'}
          message={`重设 ${displayName(product)} ${dayPhrase}的数据？将清除${dayPhrase}的生产/售出/报废/调拨记录（保留一条重设记录，注明操作人）。余额会回到当日开始前的结存，不可撤销。`}
          onConfirm={doReset}
          onClose={() => { if (!resetting) setConfirmReset(false); }} />
      )}

      {confirmStock && (
        <ConfirmDialog
          title="重设库存"
          kind="danger"
          confirmLabel={resetting ? '重设中…' : '确认清零'}
          message={`重设 ${displayName(product)} 的库存？将把所有地点的当前结存清零（通过一条「调整」记录抵消，完整保留历史与报表）。不可撤销。`}
          onConfirm={doResetStock}
          onClose={() => { if (!resetting) setConfirmStock(false); }} />
      )}
    </>
  );
};

const ActivityItem = ({ e }) => {
  // A negative `produced` is a 撤销生产 (reverse production), not a normal 生产.
  const verb = (e.kind === 'produced' && e.qty < 0) ? '撤销生产' : (KIND_LABEL[e.kind] || e.kind);
  const qty = Math.abs(e.qty);
  const actor = e.actorName || '系统';
  let suffix = `@ ${locationLabel(e.location)}`;
  if (e.kind === 'transferred') {
    suffix = e.qty < 0 ? `(${locationLabel(e.location)} 出)` : `(→ ${locationLabel(e.location)} 入)`;
  }
  const reasonStr = e.reason ? ` · ${REASON_LABEL[e.reason] || e.reason}` : '';
  // 店员取走 names the staff who took the stock (joined server-side).
  const takenStr = e.reason === 'staff_taken' && e.takenByName ? ` · ${e.takenByName}` : '';
  // 其他 shows the free-text note explaining the waste.
  const noteStr = e.reason === 'other' && e.note ? ` · ${e.note}` : '';
  return (
    <li className={`inv-feed-item ${e.kind}`}>
      <strong>{actor}</strong> {verb} <strong>{qty}</strong> 件 {suffix}
      {reasonStr}{takenStr}{noteStr}
      <span className="inv-feed-time">{formatTime(e.createdAt)}</span>
    </li>
  );
};

const ResetItem = ({ r }) => {
  const actor = r.actorName || '系统';
  const t = r.cleared?.totals;
  const parts = [];
  if (t) {
    if (t.produced) parts.push(`生产${t.produced}`);
    if (t.sold)     parts.push(`售出${t.sold}`);
    if (t.wasted)   parts.push(`报废${t.wasted}`);
  }
  const clearedStr = parts.length ? `清除 ${parts.join('/')}` : `清除 ${r.deletedCount} 条`;
  return (
    <li className="inv-feed-item reset">
      <strong>{actor}</strong> 重设了 <strong>{r.resetDate}</strong> 的数据 · {clearedStr}
      <span className="inv-feed-time">{formatTime(r.createdAt)}</span>
    </li>
  );
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (isNaN(d.getTime())) return iso;
  const M = d.getMonth() + 1;
  const D = d.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${M}月${D}日 ${hh}:${mm}`;
}
